<template>
  <SfInpageListItem
    :key="item.name"
    :item-id="item.name"
    :compact="true"
    :full-width-title="true"
    extra-list-item-class="tw-gap-x-6"
    data-testid="sidebar-insights-property"
  >
    <template #title>
      <span
        ref="nameRef"
        v-tooltip="nameTooltip"
        class="tw-line-clamp-2 [overflow-wrap:anywhere]"
      >
        {{ item.name }}
      </span>
    </template>

    <template #subtitles>
      <SfInpageListItemSubtitle class="tw-min-w-0">
        <SfTruncatedLabel :text="item.value" />
      </SfInpageListItemSubtitle>
    </template>

    <template #right>
      <div class="tw-flex tw-shrink-0 tw-items-center">
        <SfButtonDs
          v-tooltip.placementLeft="copyLabel"
          variant="tertiary"
          icon-only
          :icon-before="copied ? 'check-circle' : 'copy'"
          :class="{ '!tw-text-emerald-600': copied }"
          :aria-label="copyLabel"
          data-tooltip-size="xs"
          @click="copyToClipboard(item)"
          @mouseleave="resetCopyToClipboardState"
          @blur="resetCopyToClipboardState"
        />

        <SfButtonDs
          v-tooltip.placementLeft="
            I18n.t('sidebar_insights_properties.insights_tooltip')
          "
          variant="tertiary"
          icon-only
          icon-before="table"
          :aria-label="I18n.t('sidebar_insights_properties.insights_tooltip')"
          data-tooltip-size="xs"
          data-testid="insights-link"
          @click="openInsights(item)"
        />
      </div>
    </template>
  </SfInpageListItem>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { SfButtonDs } from '@silverfin/ui-components'
import SfInpageListItem from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item.vue'
import SfInpageListItemSubtitle from '@app/shared/vue/components/sf-inpage-list/sf-inpage-list-item-subtitle.vue'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'
import useDynamicTooltip from '@app/shared/vue/composables/use-dynamic-tooltip'
import type { InsightsProperty } from './types'
import { liveInputEvents } from '../reconciliation/constants'
import useI18n from '@app/shared/vue/composables/use-I18n'

const I18n = useI18n()

interface Props {
  item: InsightsProperty
  originalReconciliationTextId: number
  periodDate: string
}

const props = defineProps<Props>()

const nameRef = ref<HTMLElement | null>(null)
const nameTooltip = useDynamicTooltip(nameRef, () => props.item.name, {
  vertical: true,
})

const copied = ref(false)
const copyLabel = computed(() =>
  copied.value
    ? I18n.t('sidebar_insights_properties.copied_tooltip')
    : I18n.t('sidebar_insights_properties.copy_tooltip')
)

const copyToClipboard = (item: InsightsProperty) => {
  const text = `${item.name} | ${item.value} | ${props.originalReconciliationTextId}`
  navigator.clipboard.writeText(text)
  copied.value = true
}

function resetCopyToClipboardState(): void {
  copied.value = false
}

function openInsights(item: InsightsProperty): void {
  window.eventHub.$emit(liveInputEvents.INSIGHTS_ACTION_CLICKED, {
    liquidPath: item.name,
    reconciliationTextId: props.originalReconciliationTextId,
    periodDate: props.periodDate,
  })
}
</script>
